import { ref, computed } from '@vue/composition-api'
import CustomerApi from '@/api/waiter/Customer'
import { handleApiError } from '@/utils/errorHandler'

export const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

export default function useVisitPattern(vm) {
  const byDayOfWeek = ref([])
  const byHour = ref([])
  const heatmap = ref([])
  const summary = ref({
    totalVisits: 0,
    peakDayOfWeek: null,
    peakDayOfWeekLabel: '',
    peakDayOfWeekCount: 0,
    peakHour: null,
    peakHourLabel: '',
    peakHourCount: 0,
  })
  const loading = ref(false)
  const startDate = ref(null)
  const endDate = ref(null)

  const hasData = computed(() => summary.value.totalVisits > 0)

  const peakDayInfo = computed(() => {
    if (!summary.value.peakDayOfWeekLabel) return null

    return {
      label: summary.value.peakDayOfWeekLabel,
      count: summary.value.peakDayOfWeekCount,
    }
  })

  const peakHourInfo = computed(() => {
    if (!summary.value.peakHourLabel) return null

    return {
      label: summary.value.peakHourLabel,
      count: summary.value.peakHourCount,
    }
  })

  const fetchVisitPattern = async ({ start = null, end = null } = {}) => {
    loading.value = true
    try {
      const res = await CustomerApi.getVisitPattern({
        startDate: start,
        endDate: end,
      })

      startDate.value = res.data.startDate
      endDate.value = res.data.endDate
      byDayOfWeek.value = res.data.byDayOfWeek || []
      byHour.value = res.data.byHour || []
      heatmap.value = res.data.heatmap || []
      summary.value = res.data.summary || {
        totalVisits: 0,
        peakDayOfWeek: null,
        peakDayOfWeekLabel: '',
        peakDayOfWeekCount: 0,
        peakHour: null,
        peakHourLabel: '',
        peakHourCount: 0,
      }

      return {
        byDayOfWeek: byDayOfWeek.value,
        byHour: byHour.value,
        heatmap: heatmap.value,
        summary: summary.value,
      }
    } catch (error) {
      handleApiError(error, { $toast: vm.$toast })

      return {
        byDayOfWeek: [],
        byHour: [],
        heatmap: [],
        summary: { totalVisits: 0 },
      }
    } finally {
      loading.value = false
    }
  }

  const getDayLabel = dayOfWeek => DAY_LABELS[dayOfWeek] || ''

  const getHourLabel = hour => `${hour}:00`

  const getHeatmapColor = count => {
    if (count === 0) return '#f5f5f5'
    if (count <= 2) return '#c8e6c9'
    if (count <= 5) return '#81c784'
    if (count <= 10) return '#4caf50'

    return '#2e7d32'
  }

  return {
    byDayOfWeek,
    byHour,
    heatmap,
    summary,
    loading,
    startDate,
    endDate,
    hasData,
    peakDayInfo,
    peakHourInfo,
    fetchVisitPattern,
    getDayLabel,
    getHourLabel,
    getHeatmapColor,
    DAY_LABELS,
  }
}
